import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, useMotionValue, useTransform } from "framer-motion";
import { Volume2, VolumeX, ChevronDown, Hand } from "lucide-react";

const REWARD_ICON = {
  coins: "🪙",
  popsicle: "🍭",
  popcorn: "🍿",
  discount: "🎟",
  bogo: "🎁",
  mystery: "🎲",
  festival: "🎉",
  custom: "✨",
  nothing: "🥲",
};

const STORAGE_TUTORIAL = "pp_pop_reveal_seen_v1";
const STORAGE_SOUND = "pp_pop_reveal_sound";

/**
 * Pop Reveal™ — Pop Panda's signature reveal experience.
 * The customer slides the popsicle stick down; frost cracks spread, ice
 * particles fall, cold mist appears, a warm glow builds from underneath.
 * When ~60% dragged, the popsicle splits cinematically and the reward rises.
 */
export default function PopReveal({ reward, onRevealed, initiallyRevealed = false }) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 320, h: 460 });
  const [revealed, setRevealed] = useState(initiallyRevealed);
  const [splitting, setSplitting] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const audioCtxRef = useRef(null);
  const dragActive = useRef(false);
  const dragStartY = useRef(0);
  const y = useMotionValue(0);              // stick drag offset (0 = original, ~size.h*0.55 = full)
  const [pct, setPct] = useState(0);

  // Derived values for the visuals
  const frostOpacity = useTransform(y, (v) => Math.max(0, 1 - v / (size.h * 0.5)));
  const glowOpacity = useTransform(y, (v) => Math.min(1, v / (size.h * 0.35)));

  // First-time tutorial + sound pref
  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_TUTORIAL) && !initiallyRevealed) setShowTutorial(true);
      const s = localStorage.getItem(STORAGE_SOUND);
      if (s === "on") setSoundOn(true);
    } catch { /* ignore */ }
  }, [initiallyRevealed]);

  // Responsive sizing
  useEffect(() => {
    const measure = () => {
      const w = Math.min(340, wrapRef.current?.clientWidth || 320);
      setSize({ w, h: Math.round(w * 1.4) });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // Sound helpers (Web Audio API) — no external assets
  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch { /* ignore */ }
    }
    return audioCtxRef.current;
  };
  const playTick = () => {
    if (!soundOn) return;
    const ctx = ensureAudio();
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(1600 + Math.random() * 400, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.08);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.09);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.1);
  };
  const playPop = () => {
    if (!soundOn) return;
    const ctx = ensureAudio();
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(120, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.36);
  };

  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      try { localStorage.setItem(STORAGE_SOUND, next ? "on" : "off"); } catch { /* ignore */ }
      if (next) ensureAudio();
      return next;
    });
  };

  const finishReveal = useCallback(() => {
    if (revealed) return;
    setRevealed(true);
    setSplitting(true);
    try { localStorage.setItem(STORAGE_TUTORIAL, "1"); } catch { /* ignore */ }
    if (navigator.vibrate) navigator.vibrate([12, 30, 60]);
    playPop();
    setTimeout(() => onRevealed?.(), 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  // Drag handlers
  const dismissTutorial = () => { setShowTutorial(false); try { localStorage.setItem(STORAGE_TUTORIAL, "1"); } catch { /* ignore */ } };
  const getY = (e) => {
    if (e.touches && e.touches[0]) return e.touches[0].clientY;
    return e.clientY;
  };
  const onStart = (e) => {
    if (revealed) return;
    dragActive.current = true;
    dragStartY.current = getY(e) - y.get();
    dismissTutorial();
  };
  const onMove = (e) => {
    if (!dragActive.current || revealed) return;
    e.preventDefault?.();
    const rawY = getY(e) - dragStartY.current;
    const max = size.h * 0.55;
    const v = Math.max(0, Math.min(max, rawY));
    y.set(v);
    const p = Math.round((v / max) * 100);
    if (Math.abs(p - pct) >= 5) {
      setPct(p);
      if (navigator.vibrate) navigator.vibrate(6);
      playTick();
    }
    if (p >= 92) finishReveal();
  };
  const onEnd = () => {
    dragActive.current = false;
    if (!revealed) {
      // Spring back partially — but keep some progress
      const cur = y.get();
      if (cur < size.h * 0.15) y.set(0);
    }
  };

  // Cinematic split — auto-runs when revealed
  useEffect(() => {
    if (!revealed) return;
    y.set(size.h * 0.6);
    setPct(100);
    // Un-mount splitting after animation
    const t = setTimeout(() => setSplitting(false), 1800);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [revealed]);

  return (
    <div
      ref={wrapRef}
      className="relative w-full max-w-sm mx-auto select-none"
      data-testid="pop-reveal"
    >
      {/* Sound toggle */}
      <button
        type="button"
        onClick={toggleSound}
        aria-label={soundOn ? "Mute sounds" : "Enable sounds"}
        className="absolute top-0 right-0 z-30 rounded-full bg-white/10 border border-white/20 backdrop-blur-md p-2 text-brand-cream/80 hover:text-brand-cream hover:bg-white/20 transition"
        data-testid="pop-reveal-sound-toggle"
      >
        {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
      </button>

      <div
        className="relative mx-auto"
        style={{ width: size.w, height: size.h }}
      >
        {/* Cold mist */}
        <motion.div
          className="absolute inset-x-0 -top-3 h-24 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(200,235,255,0.5) 0%, rgba(200,235,255,0.15) 45%, transparent 75%)",
            filter: "blur(10px)",
            opacity: glowOpacity,
          }}
        />

        {/* Ambient glow (behind popsicle) */}
        <motion.div
          className="absolute inset-0 pointer-events-none rounded-full"
          style={{
            background:
              "radial-gradient(circle at 50% 55%, rgba(255,224,122,0.85) 0%, rgba(255,201,60,0.45) 25%, rgba(255,79,90,0.15) 55%, transparent 75%)",
            filter: "blur(18px)",
            opacity: glowOpacity,
          }}
        />

        {/* Falling ice particles */}
        {!revealed && Array.from({ length: 14 }).map((_, i) => (
          <motion.span
            key={i}
            className="absolute pointer-events-none"
            style={{
              left: `${10 + (i * 79) % 80}%`,
              top: `${20 + (i * 37) % 30}%`,
              width: 3 + (i % 3),
              height: 3 + (i % 3),
              borderRadius: "50%",
              background: "rgba(220,240,255,0.85)",
              boxShadow: "0 0 6px rgba(180,220,255,0.6)",
              opacity: Math.max(0, Math.min(1, (pct - 15) / 80)),
            }}
            animate={pct > 15 ? {
              y: [0, size.h * 0.4],
              opacity: [0.9, 0],
              rotate: [0, 180],
            } : {}}
            transition={{
              duration: 1.6 + (i % 4) * 0.35,
              repeat: Infinity,
              delay: (i % 6) * 0.15,
              ease: "easeIn",
            }}
          />
        ))}

        {/* Popsicle SVG (with reveal underlay) */}
        <div className="absolute inset-0 flex justify-center">
          <PopsicleArt
            w={size.w}
            h={size.h}
            pct={pct}
            splitting={splitting}
            reward={reward}
            frostOpacity={frostOpacity}
            glowOpacity={glowOpacity}
          />
        </div>

        {/* Draggable stick handle (overlay bounds — bottom of popsicle) */}
        {!revealed && (
          <motion.div
            className="absolute left-1/2 z-20 cursor-grab active:cursor-grabbing touch-none"
            style={{
              y,
              top: size.h * 0.68,
              width: 84,
              height: 100,
              marginLeft: -42,
            }}
            onMouseDown={onStart}
            onMouseMove={onMove}
            onMouseUp={onEnd}
            onMouseLeave={onEnd}
            onTouchStart={(e) => { e.preventDefault(); onStart(e); }}
            onTouchMove={(e) => { e.preventDefault(); onMove(e); }}
            onTouchEnd={onEnd}
            animate={{ y: [0, 4, 0] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            data-testid="pop-reveal-stick"
          >
            {/* Visual affordance ring */}
            <motion.div
              className="absolute inset-0 rounded-3xl pointer-events-none"
              animate={{ boxShadow: ["0 0 0 0 rgba(255,201,60,0.6)", "0 0 0 16px rgba(255,201,60,0)"] }}
              transition={{ duration: 1.8, repeat: Infinity }}
            />
            {/* Arrow hint */}
            <motion.div
              className="absolute inset-x-0 -bottom-6 flex justify-center text-brand-yellow"
              animate={{ y: [0, 6, 0], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.4, repeat: Infinity }}
            >
              <ChevronDown size={22} />
            </motion.div>
          </motion.div>
        )}

        {/* Progress pill */}
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-30">
          <div className="rounded-full bg-black/50 border border-brand-yellow/40 backdrop-blur-md px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-brand-yellow font-semibold">
            {revealed ? "Revealed" : `${pct}% opened`}
          </div>
        </div>
      </div>

      {/* First-time tutorial */}
      <AnimatePresence>
        {showTutorial && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm grid place-items-center px-6"
            onClick={dismissTutorial}
            data-testid="pop-reveal-tutorial"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="max-w-xs w-full rounded-3xl p-6 text-center text-brand-cream"
              style={{ background: "linear-gradient(160deg, #1e0d3e 0%, #0e0a24 100%)", border: "1px solid rgba(255,201,60,0.35)" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-[10px] uppercase tracking-[0.32em] text-brand-yellow">Pop Reveal&trade;</div>
              <div className="mt-1 font-display text-xl">Slide the popsicle stick down</div>
              <div className="mt-1 text-xs text-brand-cream/70">to reveal your Pop Surprise</div>

              {/* Animated hand */}
              <div className="relative mt-5 h-32">
                <motion.div
                  className="absolute inset-x-0 top-0 flex justify-center"
                  animate={{ y: [0, 60, 0] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <div className="relative">
                    <div className="w-12 h-14 rounded-b-3xl rounded-t-xl bg-gradient-to-b from-[#F5DEB3] to-[#C68642] shadow-lg" />
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#F5DEB3]" />
                    <motion.div
                      className="absolute -top-4 -left-1 w-4 h-6 rounded-full bg-[#F5DEB3] rotate-12"
                    />
                  </div>
                </motion.div>
                {/* Stick */}
                <div className="absolute inset-x-0 top-16 flex justify-center">
                  <div className="w-3 h-16 rounded-full bg-gradient-to-b from-[#C69F6B] to-[#8B5E2B]" />
                </div>
              </div>

              <button onClick={dismissTutorial} className="mt-4 w-full rounded-full px-6 py-2.5 text-sm font-semibold text-[#1a0f2f]" style={{ background: "linear-gradient(135deg, #FFE07A 0%, #FFC93C 50%, #E5A100 100%)" }} data-testid="pop-reveal-tutorial-dismiss">
                <Hand size={14} className="inline mr-1" /> Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ==================== POPSICLE ART ==================== */
function PopsicleArt({ w, h, pct, splitting, reward, frostOpacity, glowOpacity }) {
  const bodyH = h * 0.68;
  const bodyW = w * 0.65;
  const bodyX = (w - bodyW) / 2;
  const stickW = w * 0.11;
  const stickH = h * 0.28;
  const stickX = (w - stickW) / 2;
  const stickY = bodyH - h * 0.02;

  const primary = "#FF4F5A";
  const secondary = "#FFC93C";
  const cream = "#FFF8F0";

  // How much cracked / opened
  const crackProgress = Math.min(1, pct / 100);

  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="drop-shadow-2xl">
      <defs>
        <linearGradient id="popBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={secondary} />
          <stop offset="55%" stopColor={primary} />
          <stop offset="100%" stopColor="#B8262D" />
        </linearGradient>
        <linearGradient id="popShine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="rgba(255,255,255,0)" />
          <stop offset="45%" stopColor="rgba(255,255,255,0.28)" />
          <stop offset="55%" stopColor="rgba(255,255,255,0.28)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
        <linearGradient id="popFrost" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.6)" />
          <stop offset="100%" stopColor="rgba(200,225,255,0.35)" />
        </linearGradient>
        <linearGradient id="stickGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#DFB07A" />
          <stop offset="50%" stopColor="#B48146" />
          <stop offset="100%" stopColor="#8B5E2B" />
        </linearGradient>
        <radialGradient id="innerGlow" cx="50%" cy="50%" r="55%">
          <stop offset="0%" stopColor="#FFF1B3" stopOpacity="1" />
          <stop offset="55%" stopColor="#FFC93C" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#FF9800" stopOpacity="0" />
        </radialGradient>
        <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="5" result="b" />
          <feOffset in="b" dx="0" dy="6" result="o" />
          <feColorMatrix in="o" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.35 0" />
        </filter>
        {/* Split clip */}
        <clipPath id="topHalfClip"><rect x="0" y="0" width={w} height={bodyH / 2 + 6} /></clipPath>
        <clipPath id="bottomHalfClip"><rect x="0" y={bodyH / 2 - 6} width={w} height={bodyH} /></clipPath>
      </defs>

      {/* --- INNER REWARD (underneath) --- */}
      <g style={{ opacity: splitting || crackProgress > 0.15 ? 1 : 0.15 }}>
        <circle cx={w / 2} cy={bodyH * 0.5} r={bodyW * 0.55} fill="url(#innerGlow)" opacity={0.85} />
        <foreignObject x={w * 0.2} y={bodyH * 0.3} width={w * 0.6} height={bodyH * 0.5}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 50 }}>{REWARD_ICON[reward?.type] || "🎁"}</div>
          </div>
        </foreignObject>
      </g>

      {/* --- POPSICLE BODY (splits when revealed) --- */}
      {/* Top half */}
      <motion.g
        animate={splitting ? { y: -bodyH * 0.35, rotate: -8, opacity: 0.9 } : { y: 0, rotate: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.17, 0.75, 0.35, 1] }}
        style={{ transformOrigin: `${w / 2}px ${bodyH / 2}px` }}
      >
        <g clipPath="url(#topHalfClip)">
          <PopBody bodyX={bodyX} bodyW={bodyW} bodyH={bodyH} />
          {/* Panda face branding on top */}
          <g transform={`translate(${w / 2 - 22} ${bodyH * 0.2})`}>
            <circle cx={22} cy={22} r={20} fill="#fff" opacity="0.85" />
            <circle cx={12} cy={16} r={5} fill="#2B2B2B" />
            <circle cx={32} cy={16} r={5} fill="#2B2B2B" />
            <circle cx={22} cy={30} r={3.5} fill="#2B2B2B" />
            {/* ears */}
            <circle cx={4} cy={6} r={5} fill="#2B2B2B" />
            <circle cx={40} cy={6} r={5} fill="#2B2B2B" />
          </g>
          <text x={w / 2} y={bodyH * 0.42} textAnchor="middle" fontSize={11} fontWeight="700" letterSpacing="3" fill="rgba(255,255,255,0.85)">POP PANDA</text>
        </g>
      </motion.g>
      {/* Bottom half */}
      <motion.g
        animate={splitting ? { y: bodyH * 0.35, rotate: 8, opacity: 0.9 } : { y: 0, rotate: 0, opacity: 1 }}
        transition={{ duration: 0.9, ease: [0.17, 0.75, 0.35, 1] }}
        style={{ transformOrigin: `${w / 2}px ${bodyH / 2}px` }}
      >
        <g clipPath="url(#bottomHalfClip)">
          <PopBody bodyX={bodyX} bodyW={bodyW} bodyH={bodyH} />
          <text x={w / 2} y={bodyH * 0.6} textAnchor="middle" fontSize={9} fontWeight="600" letterSpacing="2" fill="rgba(255,255,255,0.7)">
            SLIDE STICK DOWN
          </text>
        </g>
      </motion.g>

      {/* --- FROST LAYER --- */}
      <motion.g style={{ opacity: frostOpacity }}>
        {Array.from({ length: 12 }).map((_, i) => (
          <circle
            key={i}
            cx={bodyX + (i * 13.7) % bodyW}
            cy={bodyH * 0.15 + (i * 41.3) % (bodyH * 0.55)}
            r={4 + (i % 3)}
            fill="url(#popFrost)"
            opacity={0.85}
          />
        ))}
      </motion.g>

      {/* --- CRACK LINES (spread as pct grows) --- */}
      <g stroke="rgba(255,255,255,0.75)" strokeWidth="1.4" strokeLinecap="round" fill="none">
        {crackProgress > 0.1 && <path d={`M${w/2} ${bodyH*0.2} L${w/2 - 20*crackProgress} ${bodyH*0.35} L${w/2 + 10*crackProgress} ${bodyH*0.5}`} opacity={crackProgress} />}
        {crackProgress > 0.25 && <path d={`M${w/2 - 40} ${bodyH*0.3} L${w/2 - 20*crackProgress} ${bodyH*0.45*crackProgress + bodyH*0.3}`} opacity={crackProgress} />}
        {crackProgress > 0.4 && <path d={`M${w/2 + 40} ${bodyH*0.28} L${w/2 + 15*crackProgress} ${bodyH*0.48}`} opacity={crackProgress} />}
        {crackProgress > 0.5 && <path d={`M${w/2} ${bodyH*0.15} L${w/2 - 6*crackProgress} ${bodyH*0.65}`} opacity={crackProgress} />}
        {crackProgress > 0.65 && <path d={`M${w/2 - 30} ${bodyH*0.55} L${w/2 + 30*crackProgress} ${bodyH*0.65}`} opacity={crackProgress} />}
      </g>

      {/* --- STICK (draggable visual mirror; also drawn statically) --- */}
      <g transform={`translate(0, ${splitting ? h * 0.15 : Math.min(h * 0.15, pct * 0.005 * h)})`}>
        <rect x={stickX} y={stickY} width={stickW} height={stickH} rx={stickW / 2} fill="url(#stickGrad)" filter="url(#softShadow)" />
        <rect x={stickX + 2} y={stickY + 3} width={stickW - 4} height={4} rx={2} fill="rgba(255,255,255,0.35)" />
      </g>

      {/* --- SHINE overlay --- */}
      <rect x={bodyX} y={0} width={bodyW} height={bodyH} rx={bodyW / 2} fill="url(#popShine)" opacity={0.4} pointerEvents="none" />
    </svg>
  );
}

function PopBody({ bodyX, bodyW, bodyH }) {
  return (
    <g>
      <rect x={bodyX} y={0} width={bodyW} height={bodyH} rx={bodyW / 2} fill="url(#popBody)" />
      {/* subtle side shading */}
      <rect x={bodyX} y={0} width={bodyW * 0.25} height={bodyH} rx={bodyW / 2} fill="rgba(0,0,0,0.15)" />
      <rect x={bodyX + bodyW * 0.75} y={0} width={bodyW * 0.25} height={bodyH} rx={bodyW / 2} fill="rgba(255,255,255,0.12)" />
    </g>
  );
}
